import React from 'react';
import { formatPriceGBP } from '../../helpers/formatPrice';
import { MessagePropertiesContext } from '../../contexts/messageProperties';
import { V12FinanceTerm, HitachiFinanceTerm, FinanceProperties } from '../../types';

interface FinanceSummaryProps {
  depositValue: number;
  term: V12FinanceTerm | HitachiFinanceTerm;
}

function FinanceSummary({
  monthlyRepayment,
  creditAmount,
  interestPayable,
  totalPayable,
  term,
  depositValue
}: FinanceSummaryProps & FinanceProperties): React.ReactElement {
  const [formattedMessage] = React.useContext(MessagePropertiesContext);
  const deferredTime = 'deferredPeriod' in term ? term.deferredPeriod : 0;
  return (
    <>
      <dl className="finance-summary finance-summary--highlight">
        <dt
          className="finance-summary__label"
          dangerouslySetInnerHTML={{
            __html: formattedMessage({
              id: 'checkout.components.finance.summary.monthlyRepayments',
              values: { 0: term.term },
              fallback: `Monthly <span>Repayments (${term.term}):</span>`
            })
          }}
        />
        <dd className="finance-summary__value">{formatPriceGBP(monthlyRepayment, 2)}</dd>
      </dl>

      {deferredTime > 0 && (
        <dl className="finance-summary">
          <dt className="finance-summary__label">
            {formattedMessage({
              id: 'checkout.components.finance.summary.firstPayment.label',
              fallback: 'First payment in'
            })}
            :
          </dt>
          <dd
            className="finance-summary__value"
            dangerouslySetInnerHTML={{
              __html: formattedMessage({
                id: 'checkout.components.finance.summary.firstPayment.value',
                values: { 0: deferredTime },
                fallback: `${deferredTime} months`
              })
            }}
          />
        </dl>
      )}

      <dl className="finance-summary">
        <dt className="finance-summary__label">
          {formattedMessage({ id: 'checkout.components.finance.summary.deposit', fallback: 'Deposit' })}:
        </dt>
        <dd className="finance-summary__value">{formatPriceGBP(depositValue, 2)}</dd>
      </dl>

      <dl className="finance-summary">
        <dt className="finance-summary__label">
          {formattedMessage({
            id: 'checkout.components.finance.summary.totalCreditAmount',
            fallback: 'Total Credit Amount'
          })}
          :
        </dt>
        <dd className="finance-summary__value">{formatPriceGBP(creditAmount, 2)}</dd>
      </dl>

      <dl className="finance-summary">
        <dt className="finance-summary__label">
          {formattedMessage({
            id: 'checkout.components.finance.summary.interestPayable',
            fallback: 'Interest Payable'
          })}
          :
        </dt>
        <dd className="finance-summary__value">{formatPriceGBP(interestPayable, 2)}</dd>
      </dl>

      <dl className="finance-summary finance-summary--total">
        <dt className="finance-summary__label">
          {formattedMessage({ id: 'checkout.components.finance.summary.totalPayable', fallback: 'Total Payable' })}:
        </dt>
        <dd className="finance-summary__value">{formatPriceGBP(totalPayable, 2)}</dd>
      </dl>
    </>
  );
}

export default FinanceSummary;
